import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../context/AuthContext";

const TAG: {[key: string]: {bg: string; color: string}} = {
  "free":        { bg: "#E2F0D9", color: "#375623" },
  "paid":        { bg: "#FFF2CC", color: "#7F6000" },
  "comingSoon":  { bg: "#EAE6E0", color: "#5C5C5C" },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" as const } },
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [user, setUser] = useState<any>(null);
  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [recentReports, setRecentReports] = useState<any>({
    total_reports: 0, capital_gain_count: 0, bank_statement_count: 0,
    capital_gain: [], bank_statement: [],
  });
  const [reportsLoading, setReportsLoading] = useState(true);
  const quickActionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const TOOLS = [
    { titleKey: "tools.capitalGain", descKey: "tools.capitalGainDesc", icon: "📈", link: "/tools/capital-gain", tagKey: "paid" },
    { titleKey: "tools.tds", descKey: "tools.tdsDesc", icon: "🧮", link: "/tools/tds-calculator", tagKey: "free" },
    { titleKey: "tools.bankStatement", descKey: "tools.bankStatementDesc", icon: "🏦", link: "/tools/bank-statement", tagKey: "paid" },
    { titleKey: "tools.propertyGain", descKey: "tools.propertyGainDesc", icon: "🏠", link: "/tools/property-gain", tagKey: "free" },
    { titleKey: "tools.overseasTds", descKey: "tools.overseasTdsDesc", icon: "🌏", link: "/tools/overseas-tds", tagKey: "paid" },
    { titleKey: "tools.cryptoGain", descKey: "tools.cryptoGainDesc", icon: "🪙", link: "/tools/crypto-gain", tagKey: "comingSoon" },
  ];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const fetchRecentReports = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      setReportsLoading(true);
      const res = await fetch("/api/reports/recent", { headers: { Authorization: "Bearer " + token } });
      if (res.ok) setRecentReports(await res.json());
    } catch (e) {
      console.error("Failed to fetch reports:", e);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleDownload = async (id: string, type: string) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`/api/reports/download/${type}/${id}`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) { alert("Download failed"); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "report.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      localStorage.setItem("token", token);
      const pic = params.get("picture");
      if (pic) localStorage.setItem("userPicture", pic);
      window.history.replaceState({}, "", "/dashboard");
      refreshUser(); // Update global auth state so Navbar shows user
    }
    const stored = localStorage.getItem("token");
    if (!stored) { navigate("/login"); return; }
    fetch("/api/auth/me", { headers: { Authorization: "Bearer " + stored } })
      .then(r => r.json())
      .then(data => { if (data.email) { setUser(data); fetchRecentReports(); } else navigate("/login"); })
      .catch(() => navigate("/login"));
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (quickActionRef.current && !quickActionRef.current.contains(e.target as Node)) {
        setQuickActionOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: "40px", height: "40px", border: "3px solid var(--border)", borderTop: "3px solid #C9A84C", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }}></div>
        <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>{t("common.loading")}</p>
      </div>
    </div>
  );

  const firstName = (user.name || user.email || "").split(" ")[0];
  const isPaidPlan = (user.plan || "free").toLowerCase() !== "free";

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>Dashboard</span>
          </div>
          <h1>{greeting}, {firstName}</h1>
          <p className="ph-subtitle">Your tax tools and recent reports</p>
        </div>
      </div>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 24px" }}>

      {/* SECTION 2 — Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
        className="stats-row"
        style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}
      >
        {/* Card 1: Reports Generated */}
        <div
          style={{ background: "var(--card-bg)", borderRadius: "16px", border: "1.5px solid var(--border)", padding: "24px", transition: "transform 0.2s, box-shadow 0.2s" }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)"; (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLElement).style.boxShadow = "none"; }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <div style={{ width: "44px", height: "44px", background: "rgba(201,168,76,0.12)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", flexShrink: 0 }}>📊</div>
            <div style={{ fontSize: "13px", color: "var(--text-muted)", fontWeight: 500 }}>Reports Generated</div>
          </div>
          <div style={{ fontSize: "38px", fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{recentReports.total_reports}</div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "8px" }}>All time</div>
        </div>

        {/* Card 2: Current Plan */}
        <div
          style={{ background: "var(--card-bg)", borderRadius: "16px", border: "1.5px solid var(--border)", padding: "24px", transition: "transform 0.2s, box-shadow 0.2s" }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)"; (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLElement).style.boxShadow = "none"; }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <div style={{ width: "44px", height: "44px", background: "rgba(201,168,76,0.12)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", flexShrink: 0 }}>⭐</div>
            <div style={{ fontSize: "13px", color: "var(--text-muted)", fontWeight: 500 }}>Current Plan</div>
          </div>
          <div style={{ fontSize: "28px", fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{(user.plan || "FREE").toUpperCase()}</div>
          <div style={{ marginTop: "10px" }}>
            {isPaidPlan ? (
              <span style={{ fontSize: "12px", background: "#E2F0D9", color: "#375623", padding: "3px 10px", borderRadius: "6px", fontWeight: 700 }}>Active</span>
            ) : (
              <Link to="/pricing" style={{ fontSize: "12px", color: "#C9A84C", fontWeight: 600, textDecoration: "none" }}>Upgrade for unlimited →</Link>
            )}
          </div>
        </div>

        {/* Card 3: Quick Action */}
        <div
          ref={quickActionRef}
          style={{ background: "var(--card-bg)", borderRadius: "16px", border: "1.5px solid var(--border)", padding: "24px", position: "relative", cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s" }}
          onClick={() => setQuickActionOpen(o => !o)}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)"; (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLElement).style.boxShadow = "none"; }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <div style={{ width: "44px", height: "44px", background: "rgba(201,168,76,0.12)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", flexShrink: 0 }}>➕</div>
            <div style={{ fontSize: "13px", color: "var(--text-muted)", fontWeight: 500 }}>Generate a report</div>
          </div>
          <div style={{ fontSize: "26px", fontWeight: 800, color: "#C9A84C", lineHeight: 1 }}>New Report</div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "8px" }}>Click to choose type ▾</div>
          <AnimatePresence>
            {quickActionOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: "12px", padding: "8px", zIndex: 200, boxShadow: "0 8px 28px rgba(0,0,0,0.15)" }}
                onClick={e => e.stopPropagation()}
              >
                <Link to="/tools/capital-gain"
                  style={{ display: "block", padding: "10px 14px", borderRadius: "8px", textDecoration: "none", color: "var(--text-primary)", fontSize: "14px", fontWeight: 500, transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--offwhite)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  📈 Capital Gain Report
                </Link>
                <Link to="/tools/bank-statement"
                  style={{ display: "block", padding: "10px 14px", borderRadius: "8px", textDecoration: "none", color: "var(--text-primary)", fontSize: "14px", fontWeight: 500, transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--offwhite)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  🏦 Bank Statement
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* SECTION 3 — Recent Reports */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
        style={{ background: "var(--card-bg)", borderRadius: "16px", border: "1.5px solid var(--border)", padding: "24px 28px", marginBottom: "32px" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>Recent Reports</h2>
          <Link to="/account/reports" style={{ fontSize: "13px", color: "#C9A84C", fontWeight: 600, textDecoration: "none" }}>View All →</Link>
        </div>

        {reportsLoading ? (
          <div style={{ textAlign: "center", padding: "32px", color: "var(--text-muted)", fontSize: "14px" }}>Loading reports…</div>
        ) : recentReports.total_reports === 0 ? (
          <div style={{ border: "2px dashed var(--border)", borderRadius: "12px", padding: "44px 24px", textAlign: "center" }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>📂</div>
            <p style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: "6px" }}>No reports yet</p>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "20px" }}>Generate your first report using the tools below</p>
            <Link to="/tools/capital-gain" style={{ background: "#C9A84C", color: "#2C2C2C", padding: "10px 20px", borderRadius: "8px", fontWeight: 700, fontSize: "14px", textDecoration: "none" }}>
              Generate Capital Gain Report
            </Link>
          </div>
        ) : (
          <div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "var(--offwhite)" }}>
                    {["#", "Type", "Date", "Client Name", "Description", "Download", ""].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: "12px", color: "var(--text-muted)", borderBottom: "1.5px solid var(--border)", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...recentReports.capital_gain.map((r: any) => ({ ...r, _type: "CG" })),
                    ...recentReports.bank_statement.map((r: any) => ({ ...r, _type: "BS" })),
                  ]
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .slice(0, 5)
                    .map((report: any, idx: number) => (
                      <tr key={report.id} style={{ borderBottom: "0.5px solid var(--border)", background: idx % 2 === 0 ? "var(--white)" : "var(--offwhite)" }}>
                        <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{idx + 1}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ background: report._type === "CG" ? "#E2EFDA" : "#D6E4F0", color: report._type === "CG" ? "#375623" : "#1F4E79", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 700 }}>
                            {report._type === "CG" ? "📈 Capital Gain" : "🏦 Bank Statement"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: "var(--text-secondary)" }}>{report.date}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--text-primary)" }}>{report.client_name}</td>
                        <td style={{ padding: "10px 14px", color: "var(--text-secondary)", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{report.description}</td>
                        <td style={{ padding: "10px 14px" }}>
                          {report.has_file && (
                            <button onClick={() => handleDownload(report.report_id, report._type === "CG" ? "capital-gain" : "bank-statement")}
                              style={{ background: "#E2EFDA", color: "#375623", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
                              ↓ Excel
                            </button>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <Link to="/account/reports" style={{ fontSize: "12px", color: "#C9A84C", textDecoration: "none" }}>View →</Link>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", textAlign: "right" }}>
              <Link to="/account/reports" style={{ fontSize: "13px", color: "#C9A84C", fontWeight: 600, textDecoration: "none" }}>
                View All {recentReports.total_reports} Reports →
              </Link>
            </div>
          </div>
        )}
      </motion.div>

      {/* SECTION 4 — Tools Grid */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
      >
        <div style={{ marginBottom: "20px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "4px" }}>All Tools</h2>
          <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>Click any tool to get started</p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="tools-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}
        >
          {TOOLS.map((tool, idx) => {
            const tagStyle = TAG[tool.tagKey] || TAG["comingSoon"];
            const isComingSoon = tool.tagKey === "comingSoon";
            const isPaidLocked = tool.tagKey === "paid" && (user?.plan || "free").toLowerCase() === "free";
            return (
              <motion.div key={idx} variants={cardVariants}>
                <Link to={tool.link}
                  className="tool-card tool-card-animated"
                  style={{ opacity: isComingSoon ? 0.6 : 1, display: "block", textDecoration: "none" }}
                  onClick={e => {
                    if (isComingSoon) { e.preventDefault(); return; }
                    if (isPaidLocked) { e.preventDefault(); navigate("/pricing"); return; }
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <span style={{ fontSize: "40px", lineHeight: 1 }}>{tool.icon}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ background: tagStyle.bg, color: tagStyle.color, fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "8px", whiteSpace: "nowrap" }}>
                        {t(`tools.${tool.tagKey}`)}
                      </span>
                      {!isComingSoon && <span style={{ fontSize: "14px", color: "var(--text-muted)" }}>→</span>}
                    </div>
                  </div>
                  <h3 style={{ fontSize: "15px", fontWeight: 700, marginBottom: "6px", color: "var(--text-primary)" }}>{t(tool.titleKey)}</h3>
                  <p style={{ fontSize: "12px", lineHeight: "1.5", color: "var(--text-muted)" }}>{t(tool.descKey)}</p>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>
      </div>
    </div>
  );
}
